import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type Client = Schemas['ClientDto'];
export type Project = Schemas['ProjectDto'];
export type Task = Schemas['TaskDto'];
export type Tag = Schemas['TagDto'];
export type PickerProject = Schemas['PickerProjectDto'];
export type ProjectRequest = Schemas['ProjectRequest'];
export type ProjectFilter = {
  search?: string;
  clientId?: string;
  archived?: boolean;
  billable?: boolean;
  isPublic?: boolean;
  template?: boolean;
  favorites?: boolean;
};

const keys = {
  clients: (ws: string) => ['workspace', ws, 'clients'] as const,
  projects: (ws: string) => ['workspace', ws, 'projects'] as const,
  picker: (ws: string) => ['workspace', ws, 'projects', 'picker'] as const,
  tasks: (ws: string, projectId: string) => ['workspace', ws, 'projects', projectId, 'tasks'] as const,
  tags: (ws: string) => ['workspace', ws, 'tags'] as const,
};

function invalidateCatalog(qc: ReturnType<typeof useQueryClient>, ws: string) {
  void qc.invalidateQueries({ queryKey: keys.projects(ws) });
  void qc.invalidateQueries({ queryKey: keys.clients(ws) });
}

// ------------------------------------------------------------------ Clients

export function useClients(ws: string) {
  return useQuery({
    queryKey: keys.clients(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/clients', { params: { path: { workspaceId: ws } } })),
  });
}

export function useSaveClient(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['ClientRequest'] & { id?: string }) =>
      id
        ? unwrap(
            api.PATCH('/api/v1/workspaces/{workspaceId}/clients/{id}', { params: { path: { workspaceId: ws, id } }, body }),
          )
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/clients', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

export function useDeleteClient(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/clients/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

// ------------------------------------------------------------------ Projekte

export function useProjects(ws: string, filter: ProjectFilter = {}) {
  return useQuery({
    queryKey: [...keys.projects(ws), 'list', filter],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/projects', {
          params: { path: { workspaceId: ws }, query: filter },
        }),
      ),
  });
}

export function useProject(ws: string, id: string | undefined) {
  return useQuery({
    queryKey: [...keys.projects(ws), 'detail', id],
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/projects/{id}', { params: { path: { workspaceId: ws, id: id! } } })),
    enabled: !!id,
  });
}

export function useProjectPicker(ws: string) {
  return useQuery({
    queryKey: keys.picker(ws),
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/projects/picker', { params: { path: { workspaceId: ws } } })),
    staleTime: 60_000,
  });
}

export function useSaveProject(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ProjectRequest & { id?: string }) =>
      id
        ? unwrap(
            api.PATCH('/api/v1/workspaces/{workspaceId}/projects/{id}', { params: { path: { workspaceId: ws, id } }, body }),
          )
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/projects', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

export function useDeleteProject(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/projects/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

export function useSetProjectMembers(ws: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (members: Schemas['ProjectMemberDto'][]) =>
      unwrap(
        api.PUT('/api/v1/workspaces/{workspaceId}/projects/{id}/members', {
          params: { path: { workspaceId: ws, id } },
          body: members,
        }),
      ),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

export function useToggleFavorite(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) =>
      favorite
        ? unwrap(
            api.PUT('/api/v1/workspaces/{workspaceId}/projects/{id}/favorite', { params: { path: { workspaceId: ws, id } } }),
          )
        : unwrap(
            api.DELETE('/api/v1/workspaces/{workspaceId}/projects/{id}/favorite', {
              params: { path: { workspaceId: ws, id } },
            }),
          ),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

export function useDuplicateProject(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['DuplicateRequest'] & { id: string }) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/projects/{id}/duplicate', {
          params: { path: { workspaceId: ws, id } },
          body,
        }),
      ),
    onSuccess: () => invalidateCatalog(qc, ws),
  });
}

// ------------------------------------------------------------------ Tasks

export function useTasks(ws: string, projectId: string) {
  return useQuery({
    queryKey: keys.tasks(ws, projectId),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/tasks', {
          params: { path: { workspaceId: ws, projectId } },
        }),
      ),
  });
}

export function useSaveTask(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['TaskRequest'] & { id?: string }) =>
      id
        ? unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/tasks/{id}', { params: { path: { workspaceId: ws, id } }, body }))
        : unwrap(
            api.POST('/api/v1/workspaces/{workspaceId}/projects/{projectId}/tasks', {
              params: { path: { workspaceId: ws, projectId } },
              body,
            }),
          ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.tasks(ws, projectId) });
      void qc.invalidateQueries({ queryKey: keys.picker(ws) });
    },
  });
}

export function useDeleteTask(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/tasks/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tasks(ws, projectId) }),
  });
}

// ------------------------------------------------------------------ Tags

export function useTags(ws: string) {
  return useQuery({
    queryKey: keys.tags(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/tags', { params: { path: { workspaceId: ws } } })),
    staleTime: 60_000,
  });
}

export function useSaveTag(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['TagRequest'] & { id?: string }) =>
      id
        ? unwrap(api.PATCH('/api/v1/workspaces/{workspaceId}/tags/{id}', { params: { path: { workspaceId: ws, id } }, body }))
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/tags', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tags(ws) }),
  });
}

export function useDeleteTag(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/tags/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tags(ws) }),
  });
}

// ------------------------------------------------------------------ Sätze

export type Rate = Schemas['RateDto'];

export function useRates(ws: string, enabled = true) {
  return useQuery({
    queryKey: ['workspace', ws, 'rates'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/rates', { params: { path: { workspaceId: ws } } })),
    enabled,
  });
}

export function useSetRate(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['SetRateRequest']) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/rates', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace', ws, 'rates'] });
      void qc.invalidateQueries({ queryKey: ['workspace', ws, 'time-entries'] });
    },
  });
}
